<script setup>
import { computed, ref, useAttrs } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import { useKontaCallsStore } from 'dashboard/stores/kontaCalls';
import KontaVoiceApi from 'dashboard/api/channel/kontaVoice/kontaVoiceApiClient';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  phone: { type: String, default: '' },
  contactId: { type: [String, Number], required: true },
  label: { type: String, default: '' },
  icon: { type: [String, Object, Function], default: '' },
  size: { type: String, default: 'sm' },
  tooltipLabel: { type: String, default: '' },
});

defineOptions({ inheritAttrs: false });
const attrs = useAttrs();

const KONTA_VOICE_INBOX_TYPE = 'Channel::KontaVoice';
const route = useRoute();
const router = useRouter();

const { t } = useI18n();
const dialogRef = ref(null);
const isInitiatingCall = ref(false);

const inboxesList = useMapGetter('inboxes/getInboxes');

const kontaVoiceInboxes = computed(() =>
  (inboxesList.value || []).filter(
    inbox => inbox.channel_type === KONTA_VOICE_INBOX_TYPE
  )
);

const hasKontaVoiceInboxes = computed(() => kontaVoiceInboxes.value.length > 0);

const shouldRender = computed(
  () => hasKontaVoiceInboxes.value && !!props.phone
);

const navigateToConversation = conversationId => {
  const accountId = route.params.accountId;
  if (conversationId && accountId) {
    const path = frontendURL(
      conversationUrl({
        accountId,
        id: conversationId,
      })
    );
    router.push({ path });
  }
};

const isMicrophoneBlocked = async () => {
  try {
    const status = await navigator.permissions?.query({ name: 'microphone' });
    return status?.state === 'denied';
  } catch {
    return false;
  }
};

const startCall = async inboxId => {
  if (isInitiatingCall.value) return;

  if (await isMicrophoneBlocked()) {
    useAlert(t('CONVERSATION.KONTA_VOICE_CALL.MIC_PERMISSION_DENIED'));
    return;
  }

  isInitiatingCall.value = true;
  try {
    const response = await KontaVoiceApi.initiateCall(props.contactId, inboxId);
    const {
      provider_call_id: providerCallId,
      display_id: conversationId,
      contact_name: contactName,
    } = response;

    const kontaCallsStore = useKontaCallsStore();
    kontaCallsStore.addCall({
      callSid: providerCallId,
      conversationId,
      inboxId,
      callDirection: 'outbound',
      contactName: contactName || props.phone,
    });

    useAlert(t('CONVERSATION.KONTA_VOICE_CALL.CALL_INITIATED'));
    navigateToConversation(conversationId);
  } catch (error) {
    useAlert(error?.message || t('CONVERSATION.KONTA_VOICE_CALL.CALL_FAILED'));
  } finally {
    isInitiatingCall.value = false;
  }
};

const onClick = async () => {
  if (kontaVoiceInboxes.value.length > 1) {
    dialogRef.value?.open();
    return;
  }
  const [inbox] = kontaVoiceInboxes.value;
  await startCall(inbox.id);
};

const onPickInbox = async inbox => {
  dialogRef.value?.close();
  await startCall(inbox.id);
};
</script>

<template>
  <span class="contents">
    <Button
      v-if="shouldRender"
      v-tooltip.top-end="tooltipLabel || null"
      v-bind="attrs"
      :disabled="isInitiatingCall"
      :is-loading="isInitiatingCall"
      :label="label"
      :icon="icon"
      :size="size"
      @click="onClick"
    />

    <Dialog
      v-if="shouldRender && kontaVoiceInboxes.length > 1"
      ref="dialogRef"
      :title="$t('CONVERSATION.KONTA_VOICE_CALL.INBOX_PICKER.TITLE')"
      show-cancel-button
      :show-confirm-button="false"
      width="md"
    >
      <div class="flex flex-col gap-2">
        <button
          v-for="inbox in kontaVoiceInboxes"
          :key="inbox.id"
          type="button"
          class="flex items-center justify-between w-full px-4 py-2 text-left rounded-lg hover:bg-n-alpha-2"
          @click="onPickInbox(inbox)"
        >
          <div class="flex items-center gap-2">
            <span class="i-ri-phone-fill text-n-slate-10" />
            <span class="text-sm text-n-slate-12">{{ inbox.name }}</span>
          </div>
          <span v-if="inbox.phone_number" class="text-xs text-n-slate-10">
            {{ inbox.phone_number }}
          </span>
        </button>
      </div>
    </Dialog>
  </span>
</template>
